import { useTranslation } from 'react-i18next';
import { Box, Chip, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { Section } from '../../../../../components/ui/Section';
import { UserAccessLevel } from './HierarchyPicker';

interface AssignedItem {
  id: string | number;
  label: string;
}

interface AssignedAccessPanelProps {
  level: UserAccessLevel;
  tenants: AssignedItem[];
  franchises: AssignedItem[];
  stores: AssignedItem[];
  search: string;
}

// Wraps the first match of `query` in the label with a <mark>, e.g. matching a store number.
const highlightMatch = (label: string, query: string): ReactNode => {
  const trimmed = query.trim();
  if (!trimmed) return label;

  const index = label.toLowerCase().indexOf(trimmed.toLowerCase());
  if (index === -1) return label;

  return (
    <>
      {label.slice(0, index)}
      <Box component="mark"
        sx={{ bgcolor: 'warning.200', color: 'inherit', borderRadius: '2px' }}>
        {label.slice(index, index + trimmed.length)}
      </Box>
      {label.slice(index + trimmed.length)}
    </>
  );
};

const ChipRow = ({ label, items, search }: { label: string; items: AssignedItem[]; search: string }) => (
  <Box pb={4}>
    <Typography variant="textXS"
      color="text.secondary"
      sx={{ textTransform: 'uppercase', fontWeight: 500, mb: 2, display: 'block' }}>
      {label}
    </Typography>
    {items.length === 0 ? (
      <Typography variant="textSM"
        color="text.secondary">
        {'\u2014'}
      </Typography>
    ) : (
      <Stack direction="row"
        flexWrap="wrap"
        gap={1}>
        {items.map((item) => (
          <Chip
            key={item.id}
            label={highlightMatch(item.label, search)}
            size="small"
            variant="outlined"
            color="primary"
          />
        ))}
      </Stack>
    )}
  </Box>
);

/** Read-only summary of current access assignments, drilled down from the selected level to its franchises/stores. */
const AssignedAccessPanel = ({
  level, tenants, franchises, stores, search,
}: AssignedAccessPanelProps) => {
  const { t } = useTranslation('manageUsers');

  const items = level === 'tenant' ? tenants : level === 'franchise' ? franchises : stores;

  return (
    <Section>
      <Box sx={{ p: 5, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Stack direction="row"
          alignItems="center"
          pb={3}>
          <Typography variant="textMD"
            fontWeight={600}>
            {t('assignedAccess', 'Assigned access')}
          </Typography>
        </Stack>

        {items.length === 0 ? (
          <Typography variant="textSM"
            color="text.secondary">
            {t('noAccessAssigned', 'No access assigned yet')}
          </Typography>
        ) : (
          <>
            {level === 'tenant' && <ChipRow label={t('tenants', 'Tenants')}
              items={tenants}
              search={search} />}
            {level !== 'store' && <ChipRow label={t('franchises', 'Franchises')}
              items={franchises}
              search={search} />}
            <ChipRow label={t('stores')}
              items={stores}
              search={search} />
          </>
        )}
      </Box>
    </Section>
  );
};

export default AssignedAccessPanel;
